import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ChartBlock, ChartSeries } from "../contract/types";
import { seriesColor, useChartTokens, withAlpha, type ChartTokens } from "../theme/chartTokens";
import { remToSvgUnits } from "../lib/rem";
import { formatCompact } from "../lib/format";
import type { BlockViewProps } from "./types";

/**
 * Gráfico de series (recharts).
 *
 * Reglas que se aplican aquí y no se negocian:
 *   - un solo eje: nunca dos escalas en el mismo plano;
 *   - marcas finas (línea de 2, barra de 24 como máximo) y rejilla recesiva;
 *   - el color identifica a la serie, nunca a su posición en el ranking;
 *   - con dos o más series hay leyenda siempre; con una sola, no hay leyenda
 *     porque el título ya dice qué se está mirando;
 *   - todo gráfico lleva su tabla de datos a un clic: el color no es el único
 *     canal por el que se puede leer la cifra.
 *
 * Geometría: recharts dibuja en un espacio sin unidades, así que cada grosor
 * pasa por `remToSvgUnits` — la medida se piensa en `rem` y se cruza una sola
 * vez, en la frontera del SVG.
 */

const STROKE_LINE = remToSvgUnits(0.125); // 2
const STROKE_HAIRLINE = remToSvgUnits(0.0625); // 1
const MARKER_RADIUS = remToSvgUnits(0.25); // 4 → 8 de diámetro
const BAR_MAX = remToSvgUnits(1.5); // 24
const CORNER = remToSvgUnits(0.25); // 4
const TICK_SIZE = remToSvgUnits(0.6875); // 11

interface Point {
  label: string;
  [series: string]: string | number | null;
}

interface WaterfallRow {
  label: string;
  base: number;
  positive: number | null;
  negative: number | null;
  /** Recharts indexa por `dataKey`; la firma de índice mantiene el tipo abierto. */
  [key: string]: string | number | null;
}

type ChartKind = NonNullable<ChartBlock["chart"]>;

function buildPoints(xLabels: string[], series: ChartSeries[]): Point[] {
  return xLabels.map((label, index) => {
    const point: Point = { label };
    for (const item of series) {
      const value = item.data[index];
      point[item.name] = typeof value === "number" && Number.isFinite(value) ? value : null;
    }
    return point;
  });
}

/**
 * Waterfall: barras apiladas con una base invisible.
 * Cada columna se dibuja desde el acumulado anterior, así que el salto entre
 * columnas es lo que se lee. Una última columna cuyo rótulo hable de total se
 * pinta entera desde cero (es el cierre, no un incremento más).
 */
function buildWaterfall(xLabels: string[], series: ChartSeries[]): {
  rows: WaterfallRow[];
  hasTotalColumn: boolean;
} {
  const deltas = series[0]?.data ?? [];
  const rows: WaterfallRow[] = [];
  let cumulative = 0;
  let hasTotalColumn = false;

  xLabels.forEach((label, index) => {
    const raw = deltas[index];
    const delta = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
    const isTotal = index === xLabels.length - 1 && /total|cierre|final/i.test(label);

    if (isTotal) {
      hasTotalColumn = true;
      rows.push({
        label,
        base: Math.min(0, cumulative),
        positive: cumulative >= 0 ? cumulative : null,
        negative: cumulative < 0 ? Math.abs(cumulative) : null,
      });
      return;
    }

    if (delta >= 0) {
      rows.push({ label, base: cumulative, positive: delta, negative: null });
    } else {
      const next = cumulative + delta;
      rows.push({ label, base: next, positive: null, negative: Math.abs(delta) });
    }
    cumulative += delta;
  });

  return { rows, hasTotalColumn };
}

/** Etiqueta directa solo en el último punto: una cifra por serie, no veinte. */
function lastValueLabel(total: number) {
  return function renderLabel(props: {
    x?: unknown;
    y?: unknown;
    value?: unknown;
    index?: number;
  }) {
    if (props.index !== total - 1) return null;
    const value = typeof props.value === "number" ? props.value : Number(props.value);
    if (!Number.isFinite(value)) return null;
    const x = typeof props.x === "number" ? props.x : Number(props.x ?? 0);
    const y = typeof props.y === "number" ? props.y : Number(props.y ?? 0);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return (
      <text
        x={x}
        y={y - remToSvgUnits(0.5)}
        textAnchor="end"
        className="chart__direct-label"
        fill="currentColor"
      >
        {formatCompact(value)}
      </text>
    );
  };
}

function axisProps(tokens: ChartTokens) {
  return {
    tick: { fill: tokens.label, fontSize: TICK_SIZE },
    axisLine: { stroke: tokens.axis, strokeWidth: STROKE_HAIRLINE },
    tickLine: false,
  };
}

/**
 * Escala del eje con números redondos.
 *
 * Recharts elige los cortes por su cuenta y acaba rotulando 6 500 o 19 500,
 * que en un gráfico financiero obligan a hacer cuentas para leerlo. Aquí se
 * busca el paso «bonito» (1, 2, 2.5 o 5 por potencia de diez) que cubre el
 * rango con unos cinco cortes, y se rotula con separador de miles.
 */
function niceStep(rawStep: number): number {
  if (!(rawStep > 0)) return 1;
  const exponent = Math.floor(Math.log10(rawStep));
  const magnitude = 10 ** exponent;
  const normalized = rawStep / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

function niceTicks(min: number, max: number, targetTicks = 6): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const step = niceStep((max - min) / targetTicks);
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = start; value <= end + step / 2; value += step) {
    // Redondeo a la precisión del paso: evita 0.30000000000000004.
    ticks.push(Number(value.toFixed(10)));
  }
  return ticks;
}

/** Rango de los valores dibujados, con el cero incluido si las barras lo piden. */
function valueRange(points: Point[], series: ChartSeries[], fromZero: boolean): [number, number] {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    for (const item of series) {
      const value = point[item.name];
      if (typeof value !== "number" || !Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (fromZero) {
    min = Math.min(0, min);
    max = Math.max(0, max);
  }
  return [min, max];
}

function formatTick(value: number): string {
  return Number.isFinite(value) ? formatCompact(value) : "";
}

function tooltipProps(tokens: ChartTokens) {
  return {
    cursor: { stroke: tokens.axis, strokeWidth: STROKE_HAIRLINE },
    contentStyle: {
      background: tokens.tooltipSurface,
      border: `0.0625rem solid ${tokens.grid}`,
      borderRadius: "0.5rem",
      fontSize: "0.8125rem",
      boxShadow: "var(--shadow-md)",
      padding: "0.5rem 0.75rem",
      color: tokens.ink,
    },
    labelStyle: { color: tokens.inkSecondary, fontSize: "0.75rem", marginBottom: "0.25rem" },
    itemStyle: { color: tokens.ink, padding: 0 },
    // Recharts pasa el valor crudo de la serie: números para las nuestras.
    formatter: (value: unknown) =>
      typeof value === "number" && Number.isFinite(value)
        ? formatCompact(value)
        : String(value ?? ""),
  };
}

export function Chart({ block }: BlockViewProps<ChartBlock>) {
  const tokens = useChartTokens();
  const [showTable, setShowTable] = useState(false);

  const kind: ChartKind = block.chart ?? "line";
  const series = block.series ?? [];
  const xLabels = block.x_labels ?? [];

  const points = useMemo(() => buildPoints(xLabels, series), [xLabels, series]);
  const waterfall = useMemo(
    () => (kind === "waterfall" ? buildWaterfall(xLabels, series) : null),
    [kind, xLabels, series],
  );

  // Las barras crecen desde una línea base, así que su eje tiene que incluir el
  // cero; una línea o un área pueden permitirse el rango justo de los datos.
  const fromZero = kind === "bar" || kind === "waterfall";
  const axisTicks = useMemo(() => {
    const [min, max] = valueRange(points, series, fromZero);
    return niceTicks(min, max);
  }, [points, series, fromZero]);

  if (series.length === 0 || xLabels.length === 0) {
    return <p className="atom__empty">El gráfico no trae series ni etiquetas.</p>;
  }

  const colors = series.map((item, index) => seriesColor(tokens, index, item.color_token));
  const showLegend = series.length > 1;
  // El waterfall se dibuja sobre su propia tabla apilada; el resto, sobre los puntos.
  const chartData: Point[] = waterfall ? waterfall.rows : points;

  const axisDomain: [number, number] = [
    axisTicks[0] ?? 0,
    axisTicks[axisTicks.length - 1] ?? 1,
  ];

  const common = {
    data: chartData,
    margin: { top: remToSvgUnits(0.5), right: remToSvgUnits(1), bottom: 0, left: 0 },
  };
  const shared = tooltipProps(tokens);

  return (
    <figure className="atom atom--chart chart">
      <div className="chart__plot">
        <ResponsiveContainer width="100%" height="100%">
          {kind === "bar" || kind === "waterfall" ? (
            <BarChart {...common}>
              <CartesianGrid stroke={tokens.grid} strokeWidth={STROKE_HAIRLINE} vertical={false} />
              <XAxis dataKey="label" {...axisProps(tokens)} interval="preserveStartEnd" />
              <YAxis {...axisProps(tokens)} width={remToSvgUnits(3.5)} domain={axisDomain} ticks={axisTicks} tickFormatter={formatTick} />
              <Tooltip {...shared} />
              {showLegend && !waterfall ? <Legend {...legendProps(tokens)} /> : null}
              {block.reference_lines?.map((value, index) => (
                <ReferenceLine
                  key={`ref-${index}`}
                  y={value}
                  stroke={tokens.axis}
                  strokeWidth={STROKE_HAIRLINE}
                  strokeDasharray={`${remToSvgUnits(0.25)} ${remToSvgUnits(0.25)}`}
                />
              ))}
              {waterfall ? (
                <>
                  <Bar dataKey="base" stackId="waterfall" fill="transparent" isAnimationActive={false} />
                  <Bar
                    dataKey="positive"
                    stackId="waterfall"
                    fill={tokens.series[0] ?? colors[0]}
                    maxBarSize={BAR_MAX}
                    radius={[CORNER, CORNER, 0, 0]}
                  />
                  <Bar
                    dataKey="negative"
                    stackId="waterfall"
                    fill={tokens.series[7] ?? colors[0]}
                    maxBarSize={BAR_MAX}
                    radius={[CORNER, CORNER, 0, 0]}
                  />
                </>
              ) : (
                series.map((item, index) => (
                  <Bar
                    key={item.name}
                    dataKey={item.name}
                    fill={colors[index]}
                    maxBarSize={BAR_MAX}
                    radius={[CORNER, CORNER, 0, 0]}
                  >
                    {series.length === 1 ? (
                      <LabelList dataKey={item.name} content={lastValueLabel(points.length)} />
                    ) : null}
                  </Bar>
                ))
              )}
            </BarChart>
          ) : kind === "area" ? (
            <AreaChart {...common}>
              <defs>
                {series.map((item, index) => (
                  <linearGradient
                    key={item.name}
                    id={`area-${block.id}-${index}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={colors[index]} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={colors[index]} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke={tokens.grid} strokeWidth={STROKE_HAIRLINE} vertical={false} />
              <XAxis dataKey="label" {...axisProps(tokens)} interval="preserveStartEnd" />
              <YAxis {...axisProps(tokens)} width={remToSvgUnits(3.5)} domain={axisDomain} ticks={axisTicks} tickFormatter={formatTick} />
              <Tooltip {...shared} />
              {showLegend ? <Legend {...legendProps(tokens)} /> : null}
              {block.reference_lines?.map((value, index) => (
                <ReferenceLine
                  key={`ref-${index}`}
                  y={value}
                  stroke={tokens.axis}
                  strokeWidth={STROKE_HAIRLINE}
                />
              ))}
              {series.map((item, index) => (
                <Area
                  key={item.name}
                  type="monotone"
                  dataKey={item.name}
                  stroke={colors[index]}
                  strokeWidth={STROKE_LINE}
                  fill={
                    series.length === 1
                      ? withAlpha(colors[index] ?? tokens.ink, 0.1)
                      : `url(#area-${block.id}-${index})`
                  }
                  connectNulls
                  dot={{ r: MARKER_RADIUS, fill: colors[index], stroke: tokens.surface, strokeWidth: STROKE_LINE }}
                  activeDot={{ r: MARKER_RADIUS + 1, stroke: tokens.surface, strokeWidth: STROKE_LINE }}
                />
              ))}
            </AreaChart>
          ) : (
            <LineChart {...common}>
              <CartesianGrid stroke={tokens.grid} strokeWidth={STROKE_HAIRLINE} vertical={false} />
              <XAxis dataKey="label" {...axisProps(tokens)} interval="preserveStartEnd" />
              <YAxis {...axisProps(tokens)} width={remToSvgUnits(3.5)} domain={axisDomain} ticks={axisTicks} tickFormatter={formatTick} />
              <Tooltip {...shared} />
              {showLegend ? <Legend {...legendProps(tokens)} /> : null}
              {block.reference_lines?.map((value, index) => (
                <ReferenceLine
                  key={`ref-${index}`}
                  y={value}
                  stroke={tokens.axis}
                  strokeWidth={STROKE_HAIRLINE}
                  strokeDasharray={`${remToSvgUnits(0.25)} ${remToSvgUnits(0.25)}`}
                />
              ))}
              {series.map((item, index) => (
                <Line
                  key={item.name}
                  type="monotone"
                  dataKey={item.name}
                  stroke={colors[index]}
                  strokeWidth={STROKE_LINE}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  connectNulls
                  dot={{ r: MARKER_RADIUS, fill: colors[index], stroke: tokens.surface, strokeWidth: STROKE_LINE }}
                  activeDot={{ r: MARKER_RADIUS + 1, stroke: tokens.surface, strokeWidth: STROKE_LINE }}
                >
                  {series.length === 1 ? (
                    <LabelList dataKey={item.name} content={lastValueLabel(points.length)} />
                  ) : null}
                </Line>
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {block.x_label || block.y_label ? (
        <p className="chart__axis-note">
          {block.y_label ? <span className="chart__axis-label">Eje Y: {block.y_label}</span> : null}
          {block.x_label ? <span className="chart__axis-label">Eje X: {block.x_label}</span> : null}
        </p>
      ) : null}

      {block.caption ? <figcaption className="block-caption">{block.caption}</figcaption> : null}

      <div className="chart__actions">
        <button
          type="button"
          className="chart__toggle u-button u-button--ghost u-button--small"
          aria-expanded={showTable}
          onClick={() => setShowTable((value) => !value)}
        >
          {showTable ? "Ocultar datos" : "Ver datos"}
        </button>
      </div>

      {showTable ? (
        <div className="chart__table-wrapper">
          <table className="chart__table">
            <thead>
              <tr>
                <th scope="col">{block.x_label ?? "Periodo"}</th>
                {series.map((item, index) => (
                  <th key={item.name} scope="col">
                    <span className="chart__swatch" style={swatch(colors[index])} aria-hidden="true" />
                    {item.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {xLabels.map((label, rowIndex) => (
                <tr key={`${label}-${rowIndex}`}>
                  <th scope="row">{label}</th>
                  {series.map((item) => {
                    const value = item.data[rowIndex];
                    return (
                      <td key={item.name} className="u-nums">
                        {typeof value === "number" && Number.isFinite(value) ? formatCompact(value) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </figure>
  );
}

function legendProps(tokens: ChartTokens) {
  return {
    verticalAlign: "bottom" as const,
    height: remToSvgUnits(1.75),
    iconType: "plainline" as const,
    iconSize: remToSvgUnits(0.75),
    wrapperStyle: {
      fontSize: "0.75rem",
      color: tokens.inkSecondary,
      paddingTop: "0.25rem",
    },
  };
}

/** Solo el cuadradito de color: la identidad, nunca el texto coloreado. */
function swatch(color: string | undefined) {
  return { background: color ?? "currentColor" };
}

export default Chart;
